import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { classNames } from "primereact/utils";
import styles from "../WorkoutPlanDialog.module.scss";
import { WorkoutSection } from "../models/workoutPlanViewModel";

/** The sections in the order a workout runs through them. */
export const WORKOUT_SECTION_ORDER: WorkoutSection[] = [
  WorkoutSection.WarmUp,
  WorkoutSection.Workout,
  WorkoutSection.CoolDown,
];

export const workoutSectionLabel = (section: WorkoutSection): string => {
  switch (section) {
    case WorkoutSection.WarmUp:
      return "Загрявка";
    case WorkoutSection.CoolDown:
      return "Разпускане";
    case WorkoutSection.Workout:
    default:
      return "Тренировка";
  }
};

interface ISectionPickerProps {
  isOpen: boolean;
  current: WorkoutSection;
  onClose: () => void;
  onPick: (section: WorkoutSection) => void;
}

/** Bottom sheet that moves an exercise to the warm-up, the main workout or the cool-down. */
export const SectionPicker = ({ isOpen, current, onClose, onPick }: ISectionPickerProps) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.rest_picker_backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            className={styles.rest_picker_sheet}
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            role="dialog"
            aria-label="Част от тренировката"
          >
            <div className={styles.rest_picker_title}>Част от тренировката</div>
            <div className={styles.rest_picker_sub}>
              Премести упражнението в друга част. Суперсетът му се премахва.
            </div>
            <div className={styles.type_picker_list}>
              {WORKOUT_SECTION_ORDER.map((section) => (
                <button
                  key={section}
                  type="button"
                  className={classNames(styles.type_picker_item, {
                    [styles.type_picker_item_active]: section === current,
                  })}
                  onClick={() => {
                    if (section !== current) onPick(section);
                    onClose();
                  }}
                >
                  <div className={styles.type_picker_item_label}>
                    {workoutSectionLabel(section)}
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};
